import React, { useEffect, useMemo, useState } from 'react'
import { Col, Radio } from 'antd'
import { roleuser, test } from '@edulastic/constants'
import { isUndefined } from 'lodash'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { EduButton, EduIf, EduThen } from '@edulastic/common'
import { withNamespaces } from '@edulastic/localization'
import {
  SHOW_IMMERSIVE_READER,
  testCategoryTypes,
} from '@edulastic/constants/const/test'
import {
  StyledRow,
  Block,
  StyledRadioGroup,
  RadioWrapper,
  Title,
  StyledCol,
} from '../SimpleOptions/styled'
import DetailsTooltip from './DetailsTooltip'
import SettingContainer from './SettingsContainer'
import KeypadDropdown from './KeypadDropdown'
import { ConfirmationModal } from '../../../src/components/common/ConfirmationModal'
import { isEditAllowed } from '../../../TestSetting/utils/constants'
import { BetaTag } from '../../../../common/components/BetaTag'
import AccommodationDescription from '../../../TestPage/components/Setting/components/Container/AccommodationDescription'
import { getAccommodationsSettingOptions } from '../../../TestSetting/utils'
import { allowedToSelectMultiLanguageInTest } from '../../../src/selectors/user'
import Accommodations from './Accommodations'

const {
  accessibilities,
  accessibilitySettings,
  accommodationsSettings,
  playerSkinValues,
} = test
const { magnifier, scratchPad, skipAlert, uploadWork } = accessibilitySettings
const { immersiveReader } = accommodationsSettings

const AccommodationsGroupContainer = ({
  assignmentSettings,
  testSettings,
  isDocBased,
  freezeSettings,
  overRideSettings,
  userRole,
  featuresAvailable,
  tootltipWidth,
  windowWidth,
  premium,
  canUseImmersiveReader,
  districtTestSettings,
  allowedToSelectMultiLanguage,
  t: i18Translate,
}) => {
  const {
    showMagnifier = testSettings.showMagnifier,
    enableScratchpad = testSettings.enableScratchpad,
    enableUploadWork = testSettings.enableUploadWork,
    keypad: keyPadData = testSettings.keypad || {},
    enableSkipAlert = testSettings.enableSkipAlert,
    playerSkinType = testSettings.playerSkinType || playerSkinValues.edulastic,
    showTtsForPassages = testSettings?.showTtsForPassages,
    multiLanguageEnabled = !!testSettings.multiLanguageEnabled,
    showImmersiveReader,
    showSpeechToText,
    showTextToSpeech,
    stackedTranslation,
  } = assignmentSettings

  const [selectedKeypad, setKeypad] = useState(null)
  const [warningKeypadSelection, setKeypadWarning] = useState(false)

  const handleKeypadSelection = (value) => {
    if (!value) {
      return
    }
    if (value.type === 'item-level') {
      overRideSettings('keypad', value)
    } else {
      setKeypad(value)
      setKeypadWarning(true)
    }
  }

  const confirmKeypadSelection = (confirm = false) => {
    if (confirm === true && selectedKeypad && selectedKeypad.type) {
      overRideSettings('keypad', selectedKeypad)
    }
    setKeypadWarning(false)
  }

  const accessibilityData = [
    {
      key: magnifier.key,
      value: showMagnifier,
      description: i18Translate('accessibilitySettings.magnifier.description'),
      id: magnifier.id,
    },
    {
      key: scratchPad.key,
      value: enableScratchpad,
      description: i18Translate('accessibilitySettings.scratchPad.description'),
      id: scratchPad.id,
    },
    {
      key: skipAlert.key,
      value: enableSkipAlert,
      description: i18Translate('accessibilitySettings.skipAlert.description'),
      id: skipAlert.id,
    },
  ]
  if (districtTestSettings.enableUploadWork) {
    accessibilityData.push({
      key: uploadWork.key,
      value:
        enableUploadWork === undefined || enableUploadWork === null
          ? true
          : enableUploadWork,
      description: i18Translate('accessibilitySettings.uploadWork.description'),
    })
  }

  const isAccommodationEditAllowed = useMemo(() => {
    return (
      isEditAllowed({ testSettings: districtTestSettings }) ||
      userRole !== roleuser.TEACHER
    )
  }, [districtTestSettings, userRole])

  const isTestlet = playerSkinType?.toLowerCase() === playerSkinValues.testlet

  const {
    accommodationsData,
    accommodationSettingsData,
  } = getAccommodationsSettingOptions({
    i18translate: i18Translate,
    features: featuresAvailable,
    districtTestSettings,
    userRole,
    disabled: freezeSettings || !premium,
    premium,
    isTestlet,
    allowedToSelectMultiLanguage,
    showImmersiveReader,
    showSpeechToText,
    showTextToSpeech,
    stackedTranslation,
    isDocBased,
    showTtsForPassages,
    multiLanguageEnabled,
  })

  useEffect(() => {
    if (accommodationSettingsData.length) {
      accommodationSettingsData.forEach((acc) => {
        if (!acc.isEnabled && acc.value === undefined) {
          overRideSettings(acc.key, false)
        }
      })
    }
  }, [accommodationSettingsData])

  const isVideoQuiz =
    testSettings.testCategory === testCategoryTypes.VIDEO_BASED

  return (
    <>
      <div>
        <Block smallSize id="accommodations">
          {!isDocBased &&
            isAccommodationEditAllowed &&
            !!accommodationSettingsData.filter((a) => a.isEnabled).length && (
              <>
                <Title>Accommodations Settings</Title>
                <AccommodationDescription
                  i18translate={i18Translate}
                  isAssignFlow
                />
                <Accommodations
                  disabled={freezeSettings}
                  overRideSettings={overRideSettings}
                  premium={premium}
                  accommodationsData={accommodationsData}
                  tootltipWidth={tootltipWidth}
                  windowWidth={windowWidth}
                  i18translate={i18Translate}
                />
              </>
            )}
        </Block>
        {!isVideoQuiz && (
          <Block smallSize id="accessibility">
            {!!accessibilityData.length && (
              <>
                <Title>Accessibility</Title>
                {!isDocBased && (
                  <RadioWrapper
                    disabled={freezeSettings}
                    style={{ marginTop: '10px', marginBottom: 0 }}
                  >
                    {accessibilityData.map(
                      ({ key, value, description, id }) => (
                        <SettingContainer id={id} tooltipId={`${id}-tooltip`}>
                          <DetailsTooltip
                            width={tootltipWidth}
                            title={accessibilities[key]}
                            content={description}
                            premium={
                              key === SHOW_IMMERSIVE_READER
                                ? canUseImmersiveReader
                                : featuresAvailable[key]
                            }
                            placement="rightTop"
                            id={`${id}-tooltip`}
                          />
                          <StyledRow
                            key={accessibilities[key]}
                            style={{ width: '100%' }}
                          >
                            <Col span={10}>
                              <span style={{ fontSize: 12, fontWeight: 600 }}>
                                {accessibilities[key]}
                                <EduIf
                                  condition={key === SHOW_IMMERSIVE_READER}
                                >
                                  <EduThen>
                                    <BetaTag top="-50%" left="116px">
                                      BETA
                                    </BetaTag>
                                  </EduThen>
                                </EduIf>
                              </span>
                            </Col>

                            <Col span={14}>
                              <StyledRadioGroup
                                disabled={
                                  freezeSettings ||
                                  (key === immersiveReader.key
                                    ? !canUseImmersiveReader
                                    : !featuresAvailable[key])
                                }
                                onChange={(e) =>
                                  overRideSettings(key, e.target.value)
                                }
                                value={isUndefined(value) ? true : value}
                              >
                                <Radio value data-cy={`${key}-enable`}>
                                  ENABLE
                                </Radio>
                                <Radio value={false} data-cy={`${key}-disable`}>
                                  DISABLE
                                </Radio>
                              </StyledRadioGroup>
                            </Col>
                          </StyledRow>
                        </SettingContainer>
                      )
                    )}
                  </RadioWrapper>
                )}
              </>
            )}
            {/* Keypad settings starts */}
            <SettingContainer
              id="keypad-setting"
              tooltipId="keypad-setting-tooltip"
            >
              <DetailsTooltip
                width={tootltipWidth}
                title="Keypad"
                content="Select keypad to apply current selection to all questions in the test"
                premium={premium}
                placement="right"
                id="keypad-setting-tooltip"
              />
              <StyledRow gutter={16} mb="15p">
                <Col span={10}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                    }}
                  >
                    Keypad
                  </span>{' '}
                </Col>
                <StyledCol span={12}>
                  <KeypadDropdown
                    keypadData={keyPadData}
                    handleKeypadSelection={handleKeypadSelection}
                    testKeypadData={testSettings.keypad}
                    disabled={freezeSettings || !premium}
                  />
                </StyledCol>
                <ConfirmationModal
                  centered
                  visible={warningKeypadSelection}
                  footer={[
                    <EduButton
                      isGhost
                      onClick={() => confirmKeypadSelection(false)}
                    >
                      CANCEL
                    </EduButton>,
                    <EduButton onClick={() => confirmKeypadSelection(true)}>
                      PROCEED
                    </EduButton>,
                  ]}
                  textAlign="center"
                  onCancel={() => () => confirmKeypadSelection(false)}
                >
                  <p>
                    <b>{i18Translate('keypadSettings.warning')}</b>
                  </p>
                </ConfirmationModal>
              </StyledRow>
            </SettingContainer>
            {/* Keypad settings ends */}
          </Block>
        )}
      </div>
    </>
  )
}

const enhance = compose(
  withNamespaces('author'),
  connect((state) => ({
    allowedToSelectMultiLanguage: allowedToSelectMultiLanguageInTest(state),
  }))
)

export default enhance(AccommodationsGroupContainer)
